import { FormField, Input, Textarea } from "@design";
import type { AnyFieldApi } from "@tanstack/react-form";
import { useStore } from "@tanstack/react-form";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import { Text } from "@/components/Text";
import { formatNumber } from "@/i18n/format";
import { isHex } from "./companyForm";

/** First error once the field is touched or a submit was attempted (same rule as the form kit). */
export function useCompanyFieldError(field: AnyFieldApi): string | undefined {
  const submitted = useStore(field.form.store, (s) => s.submissionAttempts > 0);
  const { isTouched, errors } = field.state.meta;
  if (!(isTouched || submitted) || errors.length === 0) return undefined;
  const first = errors[0] as unknown;
  if (typeof first === "string") return first;
  if (first && typeof first === "object" && "message" in first) return String(first.message);
  return undefined;
}

/** Textarea with a live "n / max" counter, like the old UI's key terminology field. */
export function CountedTextareaField({
  field,
  label,
  limit,
  hint,
  placeholder,
}: Readonly<{ field: AnyFieldApi; label: string; limit: number; hint?: string; placeholder?: string }>) {
  const { t } = useTranslation("adminCompany");
  const id = useId();
  const value = (field.state.value as string) ?? "";
  const atLimit = value.length >= limit;
  return (
    <div className="flex flex-col gap-1">
      <FormField label={label} htmlFor={id} error={useCompanyFieldError(field)}>
        <Textarea
          id={id}
          name={field.name}
          rows={6}
          value={value}
          placeholder={placeholder}
          onChange={(e) => field.handleChange(e.target.value)}
          onBlur={field.handleBlur}
        />
      </FormField>
      <div className="flex justify-between gap-4">
        <Text variant="caption" tone={atLimit ? "warning" : "tertiary"}>
          {atLimit ? t("limitReached") : (hint ?? "")}
        </Text>
        <Text as="span" variant="caption" tone={atLimit ? "warning" : "tertiary"} className="shrink-0">
          {t("charCount", { used: formatNumber(value.length), max: formatNumber(limit) })}
        </Text>
      </div>
    </div>
  );
}

/** A native color swatch paired with a free-text hex input. */
export function ColorField({ field, label }: Readonly<{ field: AnyFieldApi; label: string }>) {
  const { t } = useTranslation("adminCompany");
  const id = useId();
  const value = (field.state.value as string) ?? "";
  return (
    <FormField label={label} htmlFor={id} error={useCompanyFieldError(field)}>
      <Input
        id={id}
        name={field.name}
        value={value}
        placeholder={t("branding.hexPlaceholder")}
        onChange={(e) => field.handleChange(e.target.value)}
        onBlur={field.handleBlur}
        trailingAction={
          <input
            type="color"
            aria-label={t("branding.picker", { label })}
            value={isHex(value) && value.length === 7 ? value : "#000000"}
            onChange={(e) => {
              field.handleChange(e.target.value);
              field.handleBlur();
            }}
            className="size-6 cursor-pointer rounded border border-border-subtle bg-transparent p-0"
          />
        }
      />
    </FormField>
  );
}
